import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { ClubDetailItemFragment, TeamListItemFragment } from '@amf/shared/types/graphql';
import { GetServerSideProps } from 'next';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import { useTranslate } from '@tolgee/react';
import Container from '@amf/shared/components/layout/Container';
import ClubHeader from '@amf/shared/components/club/ClubHeader';
import ClubInfo from '@amf/shared/components/club/ClubInfo';
import TeamList from '@amf/shared/components/team/TeamList';
import PlayerNews from '@amf/shared/components/player/PlayerNews';
import Content from '@amf/shared/components/content/Content';

import Layout from 'components/layout/Layout';
import Meta from 'components/Meta';
import { AppLinks } from 'utils/links';

interface ClubDetailPageProps extends NavigationProps {
  club: ClubDetailItemFragment;
  teams: TeamListItemFragment[];
}

export default function ClubDetailPage({ club, teams, navigation, partners }: ClubDetailPageProps) {
  const { t } = useTranslate();
  return (
    <div className='ClubDetailPage'>
      <Layout navigation={navigation} partners={partners}>
        <Meta title={club.name} image={club.brandUrl} />
        <ClubHeader club={club} />
        <ClubInfo club={club} prefixes={{ player: AppLinks.player }} />
        <Container>
          {club.content && <Content content={club.content} />}
          {teams.length > 0 && (
            <TeamList teams={teams} title={t('club.teams')} prefixes={{ team: AppLinks.team }} />
          )}
          {club.news.length > 0 && (
            <PlayerNews
              title={t('club.news')}
              news={club.news}
              prefixes={{ news: AppLinks.newsList }}
            />
          )}
        </Container>
      </Layout>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps<ClubDetailPageProps, { id: string }> = async ({
  params,
  locale,
  res,
}) => {
  const { id } = params;
  const { club } = await sdk.ClubDetail({ union: process.env.NEXT_PUBLIC_UNION, id });

  if (!club) return { notFound: true };
  res.setHeader('Cache-Control', 'max-age=0, s-maxage=3600');

  const { teams } = await sdk.TeamList({ union: process.env.NEXT_PUBLIC_UNION, club: club.id });

  return {
    props: {
      club,
      teams,
      navigation: await getNavigation(getLanguageLocale(locale!)),
      partners: await getPartners(),
    },
  };
};
